import {
  ccpaOptOutToPrivacyString,
  CmpApiVersion,
} from '@accedo/one-plugin-sdk';

import { modalActions } from '#/store/modal/modal.store';
import { tb } from '#/store/translation/translation.store';

import { MoreItem, SettingItemType, SettingsItem } from '../config/more.types';
import { falsyToNull } from '../utils';
import {
  KnownSettings,
  serializableItemTypes,
  SETTINGS_STORAGE_PREFIX,
  SettingsKey,
} from './settings.constants';

export const initialiseDefaultSettings = (more: MoreItem[] = []) => {
  const moreItems = more.flatMap((moreItem) => moreItem.items || []);
  const selectionListItems = moreItems.flatMap((i) => i.items || []);

  const items = [...moreItems, ...selectionListItems];
  const serializableItems = items.filter(
    (i) => serializableItemTypes.indexOf(i.type) > -1
  );

  initSettingValues(serializableItems);
};

export const saveSetting = (
  key: SettingsKey,
  value: string,
  isInitial = false
) => {
  localStorage.setItem(SETTINGS_STORAGE_PREFIX + key, value);

  // only show reload warning modal if CCPA setting has changed
  if (key === KnownSettings.Ccpa && !isInitial) {
    modalActions.showModal({
      message: tb('settings.privacy.disclaimer'),
      buttonLabel: tb('button.reload'),
      onOk: () => window.location.reload(),
    });
  }
};

export const loadSetting = (key: SettingsKey, defaultValue?: string) => {
  const storedValue = localStorage.getItem(SETTINGS_STORAGE_PREFIX + key);

  return falsyToNull(storedValue) ?? falsyToNull(defaultValue);
};

function initSettingValues(items: (SettingItemType | SettingsItem)[]) {
  items.forEach(({ key, default: defaultValue }) => {
    if (!key) {
      return;
    }
    const storedValue = loadSetting(key);

    if (key === KnownSettings.Ccpa) {
      // fix old CCPA values
      if (storedValue === 'DONTSELL') {
        saveSetting(KnownSettings.Ccpa, 'true', true);
      } else if (storedValue === 'SELL') {
        saveSetting(KnownSettings.Ccpa, 'false', true);
      }

      // early return, to avoid storing a default for CCPA, because we have to know that the uses has never set it
      return;
    }

    // store default value
    if (storedValue === null && defaultValue) {
      saveSetting(key, defaultValue.toString(), true);
      console.debug(`Settings default: ${key} = ${defaultValue}`);
    }
  });
}

// load CCPA opt out setting and convert to undefined/true/false
export const getCcpaOptOutSetting = (): undefined | boolean => {
  const stored = loadSetting(KnownSettings.Ccpa);

  if (stored === null) {
    return undefined;
  }

  return stored === 'true';
};

/**
 * Gets the US Privacy string (IAB USP format).
 *
 * USP string format: [Version][Notice][OptOut][LSPA] (4 chars)
 *  - "1NNN" — no opt-out (default / not set)
 *  - "1NYN" — user opted out of sale of personal data
 *
 * @example
 * getUsPrivacyString() // "1NNN" (never set)
 * getUsPrivacyString() // "1NNN" (not opted out)
 * getUsPrivacyString() // "1NYN" (opted out)
 * // With __uspapi present, returns CMP value e.g. "1YYN"
 */
export const getUsPrivacyString = () => {
  let uspString: string | undefined;

  // check if __uspapi is available (CMP present) and can provide a uspString for us (IAB USP API exposed by CMPs like OneTrust for example)
  if (typeof window.__uspapi === 'function') {
    window.__uspapi(
      'getUSPData',
      CmpApiVersion.UspApiVersion,
      (data: { uspString?: string }, success: boolean) => {
        if (success && data?.uspString) {
          uspString = data.uspString;
        }
      }
    );
  }

  // if we got a uspString from the CMP, use that, otherwise fall back to CCPA opt-out setting to construct a US Privacy string ourselves.
  return uspString ?? ccpaOptOutToPrivacyString(getCcpaOptOutSetting());
};
